import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Cargando, Problema, Vacio } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { conDosDecimales } from '../venta/PiezasDeVenta';

interface Asiento {
  readonly id: string;
  readonly numero: string;
  readonly fecha: string;
  readonly tipo: string;
  readonly glosa: string;
  readonly totalDebe: string;
  readonly referenciaTipo: string;
  readonly referenciaId: string;
}

interface LineaDelAsiento {
  readonly orden: number;
  readonly concepto: string;
  readonly debe: string;
  readonly haber: string;
  readonly cuentaCodigo: string;
  readonly cuentaNombre: string;
}

/**
 * El libro diario, en su forma más chica.
 *
 * Lo que esta pantalla tiene que dejar hacer es lo que R6 pide como prueba de doble vía: **de un
 * asiento llegar a la operación que lo produjo**. Un contador que ve un número raro en el mayor
 * necesita poder preguntar de dónde salió, y la respuesta no puede ser abrir la base.
 *
 * El detalle se abre en la misma pantalla y no en un diálogo: un asiento se lee comparando sus
 * renglones con los del anterior, y un diálogo tapa justamente eso.
 */
export function Asientos(): React.ReactElement {
  const { t } = useTranslation();
  const [abierto, setAbierto] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['asientos'],
    queryFn: () => pedir<Asiento[]>('/asientos'),
  });

  const detalle = useQuery({
    queryKey: ['asiento', abierto],
    enabled: abierto !== null,
    queryFn: () =>
      pedir<{ asiento: Asiento; lineas: LineaDelAsiento[] }>(`/asientos/${abierto ?? ''}`),
  });

  if (consulta.isPending) return <Cargando />;
  if (consulta.isError) return <Problema error={consulta.error} />;

  const columnas: Columna<Asiento>[] = [
    { clave: 'numero', titulo: t('asientos.numero'), contenido: (fila) => fila.numero },
    {
      clave: 'fecha',
      titulo: t('asientos.fecha'),
      contenido: (fila) => new Date(fila.fecha).toLocaleDateString(),
    },
    // Qué operación lo produjo. Con siete tipos conviviendo en el mismo diario, la glosa sola
    // obliga a leer para saber si un renglón es una venta o una devolución.
    {
      clave: 'tipo',
      titulo: t('asientos.tipo'),
      contenido: (fila) => t(`asientos.tipos.${fila.tipo}`),
    },
    { clave: 'glosa', titulo: t('asientos.glosa'), contenido: (fila) => fila.glosa },
    {
      clave: 'totalDebe',
      titulo: t('asientos.importe'),
      contenido: (fila) => conDosDecimales(fila.totalDebe),
    },
    {
      clave: 'acciones',
      titulo: '',
      contenido: (fila) => (
        <button
          type="button"
          className="rounded-marco border border-borde px-2 py-0.5 text-xs hover:bg-acento-suave"
          onClick={() => {
            setAbierto(abierto === fila.id ? null : fila.id);
          }}
        >
          {t('asientos.cuenta')}
        </button>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <Encabezado titulo={t('asientos.titulo')} descripcion={t('asientos.descripcion')} />

      <Tarjeta>
        {consulta.data.length === 0 ? (
          <Vacio titulo={t('asientos.vacio')} />
        ) : (
          <Tabla filas={consulta.data} columnas={columnas} claveDeFila={(fila) => fila.id} />
        )}
      </Tarjeta>

      {abierto === null || detalle.data === undefined ? null : (
        <Tarjeta>
          <h2 className="mb-2 text-sm font-semibold">{detalle.data.asiento.glosa}</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-texto-tenue">
                <th className="pb-1">{t('asientos.cuenta')}</th>
                <th className="pb-1 text-right">{t('asientos.debe')}</th>
                <th className="pb-1 text-right">{t('asientos.haber')}</th>
              </tr>
            </thead>
            <tbody>
              {detalle.data.lineas.map((linea) => (
                <tr key={linea.orden} className="border-t border-borde">
                  <td className="py-1">
                    <span className="numero text-xs text-texto-tenue">{linea.cuentaCodigo}</span>{' '}
                    {linea.cuentaNombre}
                  </td>
                  <td className="py-1 text-right numero">
                    {Number(linea.debe) === 0 ? '' : conDosDecimales(linea.debe)}
                  </td>
                  <td className="py-1 text-right numero">
                    {Number(linea.haber) === 0 ? '' : conDosDecimales(linea.haber)}
                  </td>
                </tr>
              ))}
              {/* Los totales van a la vista y con su marca: que cuadre es lo primero que mira
                  quien lee un asiento, y hacerlo sumar de cabeza es hacerle perder el tiempo. */}
              <tr className="border-t-2 border-borde font-semibold">
                <td className="py-1">
                  {t('asientos.totales')} <Insignia tono="bien">{t('asientos.cuadra')}</Insignia>
                </td>
                <td className="py-1 text-right numero">
                  {conDosDecimales(detalle.data.asiento.totalDebe)}
                </td>
                <td className="py-1 text-right numero">
                  {conDosDecimales(detalle.data.asiento.totalDebe)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* La vuelta a la operación: la mitad de la doble vía que R6 pide. Va para los seis
              tipos de referencia y no sólo para el comprobante: un asiento del que no se puede
              volver es exactamente el número que nadie sabe explicar. */}
          <p className="mt-3 text-xs text-texto-tenue">
            {t('asientos.verOperacion')}:{' '}
            {t(`asientos.referencias.${detalle.data.asiento.referenciaTipo}`)}{' '}
            <span className="numero">{detalle.data.asiento.referenciaId.slice(0, 8)}</span>
          </p>
        </Tarjeta>
      )}
    </section>
  );
}
